import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
    Brain,
    Sparkle,
    Rocket,
    Lightning,
    BookOpen,
    Trash,
    PlusCircle,
    ArrowsClockwise,
} from "@phosphor-icons/react";

/**
 * Custom Niche Recipe Generator — let any subscribed Operator describe their niche
 * and receive a personalized, AI-generated Recipe blueprint matching the structure
 * of curated Vault Recipes. Saved per-user for re-access.
 *
 * Hooks into POST /api/niche/generate (Gemini 3 Flash via Emergent LLM Key).
 */
export default function CustomNicheGenerator() {
    const [form, setForm] = useState({
        niche: "",
        target_audience: "",
        monetization_goal: "TikTok Creativity Program + affiliate",
        content_style: "documentary-cinematic",
    });
    const [generating, setGenerating] = useState(false);
    const [history, setHistory] = useState([]);
    const [active, setActive] = useState(null); // currently expanded blueprint
    const [loading, setLoading] = useState(true);

    const loadHistory = useCallback(async () => {
        try {
            const r = await api.get("/niche/history");
            setHistory(r.data || []);
        } catch (err) {
            // Expected: 403 if the user isn't subscribed yet (or 401 if logged out).
            // Anything else is worth surfacing in dev logs.
            if (err?.response?.status !== 403 && err?.response?.status !== 401) {
                console.warn("Niche history load failed:", err);
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadHistory(); }, [loadHistory]);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.niche.trim()) {
            toast.error("Niche is required");
            return;
        }
        setGenerating(true);
        try {
            const r = await api.post("/niche/generate", form);
            toast.success("Custom niche blueprint generated! +75 points");
            setActive(r.data);
            setForm({ ...form, niche: "", target_audience: "" });
            loadHistory();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Generation failed");
        } finally {
            setGenerating(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this custom niche blueprint?")) return;
        try {
            await api.delete(`/niche/${id}`);
            toast.success("Deleted");
            if (active?.id === id) setActive(null);
            loadHistory();
        } catch {
            toast.error("Delete failed");
        }
    };

    return (
        <section
            data-testid="custom-niche-section"
            className="bg-[#0A0A0A] border border-electric-blue/30 p-6 md:p-8 relative overflow-hidden"
        >
            <div className="absolute -top-20 -left-20 w-72 h-72 bg-electric-blue/10 rounded-full blur-3xl pointer-events-none" />
            <div className="relative">
                <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <Brain size={16} weight="fill" className="text-electric-blue" />
                            <p className="label-mono text-electric-blue">CUSTOM NICHE GENERATOR · AI</p>
                        </div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter leading-tight">
                            Build YOUR niche.<br />
                            <span className="text-electric-blue neon-text">In 10 seconds.</span>
                        </h2>
                        <p className="text-sm text-foreground/75 mt-2 max-w-xl">
                            Don't see a Vault Recipe for your exact angle? Describe your niche and the
                            Nexus Niche Architect will generate a personalized Recipe blueprint —
                            niche positioning, hook formulas, monetization paths, 30-topic Topic Bank,
                            content cadence, and a 30-day execution roadmap.
                        </p>
                    </div>
                    <div className="bg-electric-blue/10 border border-electric-blue/30 px-3 py-2">
                        <p className="label-mono text-[9px] text-electric-blue mb-0.5">EARN PER GENERATION</p>
                        <p className="font-heading text-2xl font-black text-electric-blue leading-none">+75 PTS</p>
                    </div>
                </div>

                {/* Form */}
                <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                    <Field label="YOUR NICHE *" hint="Be specific — 'history' is too broad">
                        <input
                            data-testid="niche-input"
                            type="text"
                            required
                            value={form.niche}
                            onChange={(e) => setForm({ ...form, niche: e.target.value })}
                            placeholder="e.g. Vintage NASA archival mysteries"
                            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:border-electric-blue outline-none"
                        />
                    </Field>
                    <Field label="TARGET AUDIENCE" hint="Optional — AI will infer if blank">
                        <input
                            data-testid="audience-input"
                            type="text"
                            value={form.target_audience}
                            onChange={(e) => setForm({ ...form, target_audience: e.target.value })}
                            placeholder="e.g. Men 25-45 interested in space"
                            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:border-electric-blue outline-none"
                        />
                    </Field>
                    <Field label="MONETIZATION GOAL">
                        <select
                            data-testid="monetization-select"
                            value={form.monetization_goal}
                            onChange={(e) => setForm({ ...form, monetization_goal: e.target.value })}
                            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:border-electric-blue outline-none"
                        >
                            <option>TikTok Creativity Program + affiliate</option>
                            <option>YouTube AdSense (long-form)</option>
                            <option>YouTube AdSense + tactical merch</option>
                            <option>Instagram Reels + ManyChat affiliate funnels</option>
                            <option>Sponsorships + brand deals</option>
                            <option>Mixed — all platforms, all revenue</option>
                        </select>
                    </Field>
                    <Field label="CONTENT STYLE">
                        <select
                            data-testid="style-select"
                            value={form.content_style}
                            onChange={(e) => setForm({ ...form, content_style: e.target.value })}
                            className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:border-electric-blue outline-none"
                        >
                            <option>documentary-cinematic</option>
                            <option>fast-cut viral / TikTok-native</option>
                            <option>educational explainer</option>
                            <option>storytelling / narrative-driven</option>
                            <option>tactical / no-nonsense</option>
                            <option>aesthetic / mood-driven</option>
                        </select>
                    </Field>
                    <div className="md:col-span-2">
                        <button
                            type="submit"
                            disabled={generating}
                            data-testid="generate-niche-btn"
                            className="w-full bg-electric-blue hover:bg-electric-blue-dark disabled:opacity-50 text-white font-bold py-3.5 text-xs label-mono transition-all hover:shadow-[0_0_24px_rgba(0,123,255,0.5)] flex items-center justify-center gap-2"
                        >
                            {generating ? (
                                <>
                                    <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
                                    GENERATING YOUR BLUEPRINT...
                                </>
                            ) : (
                                <>
                                    <Sparkle size={14} weight="fill" />
                                    GENERATE BLUEPRINT
                                    <Rocket size={14} weight="fill" />
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Active blueprint display */}
                {active && (
                    <div data-testid="active-blueprint" className="bg-black/40 border border-electric-blue/40 p-5 mb-5">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                            <div className="flex items-center gap-2">
                                <BookOpen size={14} weight="fill" className="text-electric-blue" />
                                <p className="label-mono text-electric-blue text-xs">{active.niche}</p>
                            </div>
                            <button
                                onClick={() => setActive(null)}
                                className="label-mono text-[10px] text-muted-foreground hover:text-white"
                            >
                                CLOSE ↑
                            </button>
                        </div>
                        <div className="prose-blueprint text-sm text-foreground/85 whitespace-pre-wrap leading-relaxed font-mono max-h-[600px] overflow-y-auto">
                            {active.blueprint}
                        </div>
                    </div>
                )}

                {/* History */}
                {!loading && history.length > 0 && (
                    <div>
                        <p className="label-mono text-[10px] text-muted-foreground mb-2">YOUR SAVED BLUEPRINTS · {history.length}</p>
                        <div className="space-y-2">
                            {history.map((h) => (
                                <div
                                    key={h.id}
                                    data-testid={`niche-history-${h.id}`}
                                    className={`flex items-center justify-between gap-3 p-3 border transition-all cursor-pointer ${
                                        active?.id === h.id
                                            ? "border-electric-blue bg-electric-blue/5"
                                            : "border-white/10 hover:border-electric-blue/40"
                                    }`}
                                    onClick={() => setActive(active?.id === h.id ? null : h)}
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{h.niche}</p>
                                        <p className="text-[10px] text-muted-foreground">
                                            {h.monetization_goal} · {new Date(h.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onDelete(h.id); }}
                                        className="text-muted-foreground hover:text-red-400 p-1.5"
                                        aria-label="Delete blueprint"
                                    >
                                        <Trash size={12} weight="bold" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                {!loading && history.length === 0 && !active && (
                    <p className="text-xs text-muted-foreground text-center py-4 italic flex items-center justify-center gap-2">
                        <PlusCircle size={12} />
                        No blueprints yet. Generate your first one above.
                    </p>
                )}
            </div>
        </section>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="label-mono text-[10px] text-muted-foreground block mb-1.5">
                {label}
                {hint && <span className="ml-2 text-[9px] text-white/40 normal-case tracking-normal">— {hint}</span>}
            </label>
            {children}
        </div>
    );
}
